import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import Button from '@amzn/meridian/button';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Table, {
  TableRow,
  TableCell,
  TableActionBar,
} from '@amzn/meridian/table';
import { type TableSortDirection } from '@amzn/meridian/table/table';
import Tag from '@amzn/meridian/tag';
import Link from '@amzn/meridian/link';
import { useTranslation } from 'src/arb-wrapper';
import { Loader } from 'src/components/common/Loader';
import { EnrichmentSkeleton } from 'src/components/common/EnrichmentSkeleton';
import { TablePagination } from 'src/components/common/data-table-action-bar/TablePagination';
import { type Filter, TokenizedSearchField } from 'src/components/common/tokenized-search-field';
import { BusinessLines, Countries } from 'src/store/constants';
import { useAvailablePublishers } from 'src/hooks/use-available-publishers';
import { getBusinessUnitDisplayName, useBusinessUnitNameMap } from 'src/hooks/useBusinessUnitNameMap';
import { type CampaignConfigurationDto, type Publisher, type Country, type BusinessLine } from 'src/store/generated/leela-api-generated';
import { useCampaignConfigurationControllerFindManyQuery } from 'src/store/leela-api';
import { FIELD_CONFIG } from 'src/components/publisher-config/PublisherAccountFields';
import { buildRegexFilters } from 'src/utils/filterUtils';
import { ConfigurationType } from 'src/constants';
import Select, { SelectOption } from '@amzn/meridian/select';
import { DateTime } from 'luxon';

export const PublisherConfigList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const availablePublishers = useAvailablePublishers();
  const {
    businessUnitNameMap,
    isLoading: isBusinessUnitsLoading,
    isError: isBusinessUnitsError,
  } = useBusinessUnitNameMap();
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    pageCount: 1,
  });

  const [sortColumn, setSortColumn] = useState('updatedAt');
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('descending');
  const [filters, setFilters] = useState<Record<string, object>>({});

  const sort = useMemo(() => {
    const dir = sortDirection === 'ascending' ? 1 : -1;
    return [`${sortColumn}:${dir}`];
  }, [sortColumn, sortDirection]);

  const [publisherFilter, setPublisherFilter] = useState<Publisher | ''>('');
  const [countryFilter, setCountryFilter] = useState<Country | ''>('');
  const [businessLineFilter, setBusinessLineFilter] = useState<BusinessLine | ''>('');

  const onSort = ({ sortColumn, sortDirection }: { sortColumn: string; sortDirection: TableSortDirection }) => {
    setPagination(prev => ({ ...prev, page: 1 }));
    setSortColumn(sortColumn);
    setSortDirection(sortDirection);
  };

  const onFiltersChanged = (newFilters: Filter[]) => {
    setPagination(prev => ({ ...prev, page: 1 }));
    setFilters(buildRegexFilters(newFilters, 'name'));
  };

  const { data, isLoading } = useCampaignConfigurationControllerFindManyQuery({
    configType: ConfigurationType.PUBLISHER_ACCOUNT,
    pageSize: pagination.pageSize,
    page: pagination.page,
    sort,
    filters,
    includeCampaignCount: false,
    ...(publisherFilter && { publisher: publisherFilter }),
    ...(countryFilter && { country: countryFilter }),
    ...(businessLineFilter && { businessLine: businessLineFilter }),
  });

  const paginationResponse = data?.meta?.pagination;
  const configs = data?.data ?? [];

  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      page: paginationResponse?.page ?? 1,
      pageCount: paginationResponse?.pageCount ?? 1,
    }));
  }, [paginationResponse]);

  return (
    <Column>
      <Heading level={1}>{t('publisher_configs')}</Heading>

      <Loader isLoading={isLoading}>
        <Table
          headerRows={1}
          showDividers
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
        >
          <TableActionBar alignmentHorizontal="justify" widths={['fill', 'fit']}>
            <Row spacing="400">
              <TokenizedSearchField
                scopes={['name']}
                onFiltersChange={onFiltersChanged}
              />
              <Select
                data-testid="publisher-config-filter-publisher"
                size="medium"
                value={publisherFilter}
                onChange={(value: string) => { setPublisherFilter(value as Publisher | ''); setPagination(prev => ({ ...prev, page: 1 })); }}
              >
                <SelectOption label={t('filter_publisher_all')} value="" />
                {availablePublishers.map(p => (
                  <SelectOption key={p} label={p} value={p} />
                ))}
              </Select>
              <Select
                data-testid="publisher-config-filter-country"
                size="medium"
                value={countryFilter}
                onChange={(value: string) => { setCountryFilter(value as Country | ''); setPagination(prev => ({ ...prev, page: 1 })); }}
              >
                <SelectOption label={t('filter_country_all')} value="" />
                {Countries.map(c => (
                  <SelectOption key={c} label={c} value={c} />
                ))}
              </Select>
              <Select
                data-testid="publisher-config-filter-business-line"
                size="medium"
                value={businessLineFilter}
                onChange={(value: string) => { setBusinessLineFilter(value as BusinessLine | ''); setPagination(prev => ({ ...prev, page: 1 })); }}
              >
                <SelectOption label={t('filter_business_line_all')} value="" />
                {Object.values(BusinessLines).map(bl => (
                  <SelectOption key={bl} label={bl} value={bl} />
                ))}
              </Select>
            </Row>
            <Button
              type="primary"
              onClick={() => { navigate('/publisher-configs/create'); }}
            >
              {t('publisher_config_create')}
            </Button>
          </TableActionBar>

          <TableRow>
            <TableCell sortColumn="name">{t('form_global_name')}</TableCell>
            <TableCell>{t('form_campaigns_publisher')}</TableCell>
            <TableCell>{t('publisher_config_business_unit')}</TableCell>
            <TableCell>{t('publisher_config_countries')}</TableCell>
            <TableCell>{t('publisher_config_business_lines')}</TableCell>
            <TableCell>{t('publisher_config_account_data')}</TableCell>
            <TableCell sortColumn="updatedAt">{t('program_header_last_updated')}</TableCell>
          </TableRow>

          {configs.map((config: CampaignConfigurationDto) => (
            <TableRow key={config.id}>
              <TableCell>
                <Link onClick={() => { navigate(`/publisher-configs/${config.id}`); }}>
                  {config.name ?? config.id}
                </Link>
              </TableCell>
              <TableCell>
                <Text>{(config.supportedPublishers ?? []).join(', ')}</Text>
              </TableCell>
              <TableCell data-testid={`publisher-config-business-unit-${config.id}`}>
                {isBusinessUnitsLoading
                  ? <EnrichmentSkeleton />
                  : <Text>
                      {getBusinessUnitDisplayName({
                        businessUnitId: config.businessUnitId,
                        businessUnitNameMap,
                        isError: isBusinessUnitsError,
                        unavailableLabel: t('publisher_config_business_unit_unavailable'),
                      })}
                    </Text>}
              </TableCell>
              <TableCell>
                <Row spacing="100" wrap="down">
                  {(config.supportedCountries ?? []).length > 0
                    ? (config.supportedCountries ?? []).map(c => (
                        <Tag key={c} type="theme">{c}</Tag>
                      ))
                    : <Text>{t('all')}</Text>}
                </Row>
              </TableCell>
              <TableCell>
                <Text>{(config.supportedBusinessLines ?? []).length > 0
                  ? (config.supportedBusinessLines ?? []).join(', ')
                  : t('all')}</Text>
              </TableCell>
              <TableCell>
                <Column spacing="100">
                  {(FIELD_CONFIG[(config.supportedPublishers ?? [])[0]] ?? []).map(f => (
                    <Text key={f.key} type="b200">{t(f.labelKey)}: {String((config.config as Record<string, unknown>)[f.key] ?? '')}</Text>
                  ))}
                </Column>
              </TableCell>
              <TableCell>
                <Text>{config.updatedAt ? DateTime.fromISO(config.updatedAt as unknown as string).toRelative() : ''}</Text>
              </TableCell>
            </TableRow>
          ))}
        </Table>

        {!isLoading && configs.length === 0 && (
          <Box spacingInset="500">
            <Column alignmentHorizontal="center" spacing="300">
              <Text alignment="center" type="b300">{t('publisher_config_empty')}</Text>
              <Button type="primary" onClick={() => { navigate('/publisher-configs/create'); }}>
                {t('publisher_config_create')}
              </Button>
            </Column>
          </Box>
        )}

        <TablePagination
          currentPage={pagination.page}
          itemsPerPage={pagination.pageSize}
          pageCount={pagination.pageCount}
          onItemsPerPageChange={pageSize => { setPagination(prev => ({ ...prev, page: 1, pageSize })); }}
          onPageChange={newPage => { setPagination(prev => ({ ...prev, page: newPage })); }}
        />
      </Loader>
    </Column>
  );
};
